import { Link, useLocation } from "react-router-dom";
import infinityLogo from "@/assets/infinity-logo.webp";
import { Mail, Phone, MapPin, Linkedin, Facebook, Instagram, Star } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { analytics } from "@/lib/analytics";

import { useState, forwardRef } from "react";
import { useNewsletterSubscribe } from "@/hooks/useNewsletterSubscribe";

const quickLinks = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Services", to: "/services" },
  { label: "Industries", to: "/industries" },
  { label: "Blog", to: "/blog" },
  { label: "Resources", to: "/resources" },
  { label: "Calculators", to: "/calculators" },
  { label: "FAQ", to: "/faq" },
  { label: "Contact", to: "/contact" },
];

const areaLinks = [
  { label: "Fordingbridge", to: "/areas/fordingbridge" },
  { label: "Ringwood", to: "/areas/ringwood" },
  { label: "Southampton", to: "/areas/southampton" },
  { label: "Romsey", to: "/areas/romsey" },
  { label: "Winchester", to: "/areas/winchester" },
  { label: "Andover", to: "/areas/andover" },
  { label: "Lymington", to: "/areas/lymington" },
  { label: "New Forest", to: "/areas/new-forest" },
  { label: "Salisbury & Wiltshire", to: "/areas/salisbury" },
  { label: "Wimborne & East Dorset", to: "/areas/wimborne" },
];

const services = [
  { label: "Bookkeeping", to: "/services/bookkeeping" },
  { label: "VAT Returns & MTD", to: "/services/vat-returns" },
  { label: "Payroll & Pensions", to: "/services/payroll-and-pensions" },
  { label: "Self Assessment", to: "/services/self-assessment" },
  { label: "Year-End Accounts", to: "/services/year-end-accounts" },
  { label: "Management Accounts", to: "/services/management-accounts" },
  { label: "Tax Planning", to: "/services/tax-planning" },
  { label: "Fractional CFO / FD", to: "/services/fractional-cfo-finance-director" },
  { label: "All services", to: "/services" },
];

export const Footer = forwardRef<HTMLElement>(function Footer(_, ref) {
  const [email, setEmail] = useState("");
  const { pathname } = useLocation();
  const { subscribe, loading: newsletterLoading } = useNewsletterSubscribe();

  return (
    <footer ref={ref} className="bg-foreground text-background">
      {/* Gradient top border */}
      <div className="h-1 gradient-orange w-full" />

      <div className="container section-padding">
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4">
          {/* Brand */}
          <div>
            <div className="mb-4">
              <img src={infinityLogo} alt="Infinity Financial Solutions" className="h-12 w-auto brightness-0 invert" />
            </div>
            <p className="text-sm leading-relaxed text-background/80">
              AAT Licensed Accountant &amp; Xero Gold Partner based near Salisbury, serving Wiltshire, the New Forest and East Dorset. Helping businesses grow with confidence.
            </p>
            <div className="mt-4 flex items-center gap-3">
              <a href="https://www.linkedin.com/company/infinityfsuk" target="_blank" rel="noopener noreferrer" onClick={() => analytics.socialClick("linkedin")} className="flex h-9 w-9 items-center justify-center rounded-lg bg-background/10 transition-all duration-300 hover:bg-primary hover:text-primary-foreground hover:scale-110" aria-label="LinkedIn">
                <Linkedin className="h-4 w-4" />
              </a>
              <a href="https://www.facebook.com/infinityfsuk" target="_blank" rel="noopener noreferrer" onClick={() => analytics.socialClick("facebook")} className="flex h-9 w-9 items-center justify-center rounded-lg bg-background/10 transition-all duration-300 hover:bg-primary hover:text-primary-foreground hover:scale-110" aria-label="Facebook">
                <Facebook className="h-4 w-4" />
              </a>
              <a href="https://www.instagram.com/infinityfsuk/" target="_blank" rel="noopener noreferrer" onClick={() => analytics.socialClick("instagram")} className="flex h-9 w-9 items-center justify-center rounded-lg bg-background/10 transition-all duration-300 hover:bg-primary hover:text-primary-foreground hover:scale-110" aria-label="Instagram">
                <Instagram className="h-4 w-4" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <p className="mb-4 font-display text-sm font-semibold uppercase tracking-wider text-primary">
              Quick Links
            </p>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className={`text-sm transition-all duration-200 hover:text-primary hover:translate-x-1 inline-block ${pathname === link.to ? 'text-primary font-semibold' : 'text-background/80'}`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <p className="mb-4 font-display text-sm font-semibold uppercase tracking-wider text-primary">
              Services
            </p>
            <ul className="space-y-2">
              {services.map((s) => (
                <li key={s.label}>
                  <Link
                    to={s.to}
                    className="text-sm text-background/80 transition-all duration-200 hover:text-primary hover:translate-x-1 inline-block"
                  >
                    {s.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact + Newsletter */}
          <div>
            <p className="mb-4 font-display text-sm font-semibold uppercase tracking-wider text-primary">
              Get in Touch
            </p>
            <div className="space-y-3">
              <div className="flex items-start gap-3 text-sm text-background/80">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <span>7 Oakridge Office Park, Southampton Road, Whaddon, Salisbury, SP5 3HT</span>
              </div>
              <div className="flex items-start gap-3 text-sm text-background/80">
                <Phone className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
               <a href="tel:01722466213" className="hover:text-primary transition-colors">01722 466213</a>
              </div>
              <div className="flex items-center gap-3 text-sm text-background/80">
                <Mail className="h-4 w-4 shrink-0 text-primary" />
                <a href="mailto:info@infinity-fs.co.uk" className="hover:text-primary transition-colors">
                  info@infinity-fs.co.uk
                </a>
              </div>
            </div>

            {/* Newsletter */}
            <div className="mt-6 border-t border-background/10 pt-4">
              <p className="mb-2 font-display text-sm font-semibold text-primary">Newsletter</p>
              <p className="text-xs text-background/80 mb-2">Monthly tax tips & business insights.</p>
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="you@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-8 border-background/20 bg-background/10 text-background text-xs placeholder:text-background/40"
                />
                <Button
                  size="sm"
                  className="gradient-orange border-0 h-8 text-xs shrink-0 hover-glow"
                  disabled={newsletterLoading}
                  onClick={async () => {
                    const ok = await subscribe(email);
                    if (ok) setEmail("");
                  }}
                >
                  {newsletterLoading ? "…" : "Go"}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Areas We Serve */}
        <div className="mt-12 border-t border-background/10 pt-8">
          <p className="text-xs font-semibold uppercase tracking-wider text-primary mb-3">Areas We Serve</p>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            {areaLinks.map((link) => (
              <Link key={link.to} to={link.to} className="text-sm text-background/80 hover:text-primary transition-colors">
                {link.label}
              </Link>
            ))}
            <Link to="/areas" className="text-sm text-primary hover:underline">
              View all areas →
            </Link>
          </div>
        </div>

        {/* The Infinity Group */}
        <div className="mt-8 border-t border-background/10 pt-8">
          <p className="text-xs font-semibold uppercase tracking-wider text-primary mb-3">The Infinity Group</p>
          <div className="grid gap-4 sm:grid-cols-2 max-w-2xl">
            <a href="https://infinity-bf.co.uk" target="_blank" rel="noopener noreferrer" className="block hover:opacity-80 transition-opacity">
               <p className="text-sm font-semibold text-background/90">Infinity Business Finance</p>
               <p className="text-xs text-background/70 mt-0.5">Business finance &amp; mortgage brokerage</p>
             </a>
             <Link to="/hr-services" className="block hover:opacity-80 transition-opacity">
               <p className="text-sm font-semibold text-background/90">Infinity Human Resources</p>
               <p className="text-xs text-background/70 mt-0.5">Practical HR advisory for growing teams</p>
             </Link>
          </div>
        </div>

        <div className="mt-8 border-t border-background/10 pt-8 flex justify-center">
          <a
            href="https://www.google.com/search?q=Infinity+Financial+Solutions+Fordingbridge"
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-2.5 rounded-full border border-background/15 bg-background/5 px-4 py-2 font-sans text-xs tracking-wide text-background/80 transition-colors hover:border-primary/40 hover:text-primary"
          >
            {/* Unicode-star fallback layer — visible if lucide SVG fails to render */}
            <span aria-hidden="true" className="flex items-center leading-none text-primary text-[13px]">
              {[...Array(5)].map((_, i) => (
                <span key={i} className="relative inline-flex h-3.5 w-3.5 items-center justify-center">
                  <span className="absolute inset-0 flex items-center justify-center">★</span>
                  <Star className="relative h-3.5 w-3.5 fill-primary text-primary" strokeWidth={0} />
                </span>
              ))}
            </span>
            <span className="font-display font-bold text-sm leading-none text-background">5.0</span>
            <span className="leading-none opacity-70">on Google reviews</span>
          </a>
        </div>

        <div className="mt-6 border-t border-background/10 pt-8 text-center text-sm text-background/70 space-y-1">
          <p>© 2022-{new Date().getFullYear()} Infinity Financial Solutions Limited. Registered in England and Wales: 14433335. All Rights Reserved.</p>
          <p>Registered Office: 7 Oakridge Office Park, Southampton Road, Whaddon, Salisbury, SP5 3HT</p>
          <div className="mt-2 flex items-center justify-center gap-3 text-xs">
            <Link to="/privacy-policy" className="text-background/80 hover:text-primary transition-all duration-200 underline underline-offset-2">
              Privacy Policy
            </Link>
            <span className="text-background/40">|</span>
            <Link to="/terms-of-service" className="text-background/80 hover:text-primary transition-all duration-200 underline underline-offset-2">
              Terms of Service
            </Link>
            <span className="text-background/40">|</span>
            <button
              onClick={() => window.dispatchEvent(new Event("open-cookie-settings"))}
              className="text-background/80 hover:text-primary transition-all duration-200 underline underline-offset-2"
            >
              Cookie Preferences
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
});
